/**
 * IELTS bands (D-105): how a band is written (half bands, one decimal, the decimal separator of
 * the interface language), the lowest CLB kept by Express Entry, and the server's rounding rule
 * (apps/academy/ielts.py `round_band`) driven by the thresholds of the versioned format table.
 */

import { type IeltsSkill, IELTS_SKILLS } from "./sitting";
import { isObject, num } from "./read";

/** The top of the IELTS scale. */
export const MAX_BAND = 9;
/** A band moves by half bands. */
export const HALF_BAND = 0.5;

function decimalSeparator(language: string): string {
  const locale = language.toLowerCase().startsWith("en") ? "en-GB" : language || "fr";
  try {
    const part = new Intl.NumberFormat(locale).formatToParts(1.5).find((p) => p.type === "decimal");
    return part?.value ?? ".";
  } catch {
    return ".";
  }
}

/**
 * A band with one decimal, like the official report, in the interface language:
 * « 6,5 » in French, « 6.5 » in English; « — » while not measured.
 */
export function formatBand(value: number | null | undefined, language: string): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  const text = value.toFixed(1);
  const separator = decimalSeparator(language);
  return separator === "." ? text : text.replace(".", separator);
}

/** The lowest CLB of the four skills (what Express Entry retains), or null until all are measured. */
export function lowestClb(values: readonly (number | null | undefined)[]): number | null {
  const known = values.filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  return known.length === values.length && known.length > 0 ? Math.min(...known) : null;
}

/** Band and CLB of every skill (null: not measured yet), from any per-skill rows. */
export function bandsBySkill(
  skills: Partial<Record<IeltsSkill, { band?: number | null; clb?: number | null } | undefined>> | null | undefined,
): { bands: Record<IeltsSkill, number | null>; clb: Record<IeltsSkill, number | null> } {
  const bands = {} as Record<IeltsSkill, number | null>;
  const clb = {} as Record<IeltsSkill, number | null>;
  for (const skill of IELTS_SKILLS) {
    const row = skills?.[skill];
    bands[skill] = typeof row?.band === "number" ? row.band : null;
    clb[skill] = typeof row?.clb === "number" ? row.clb : null;
  }
  return { bands, clb };
}

/** 0, 0.5, 1 … max (9 for every criterion of the rubric). */
export function bandSteps(max: number): number[] {
  const top = max > 0 ? max : MAX_BAND;
  return Array.from({ length: Math.floor(top / HALF_BAND) + 1 }, (_, i) => i * HALF_BAND);
}

/** Thresholds of the format table: [[0.75, 1], [0.25, 0.5], [0, 0]] (fraction → step), highest first. */
export function roundingOf(details: unknown): [number, number][] {
  const rounding = isObject(details) && isObject(details.overall_rounding) ? details.overall_rounding : {};
  const rows = Array.isArray(rounding.thresholds) ? (rounding.thresholds as unknown[]) : [];
  return rows
    .filter((row): row is unknown[] => Array.isArray(row) && row.length === 2)
    .map((row) => [num(row[0]), num(row[1])] as [number, number])
    .sort((a, b) => b[0] - a[0]);
}

/** Same rule as the server (`ielts.round_band`): the fraction of the mean against the thresholds. */
export function roundBand(value: number, thresholds: [number, number][]): number {
  const whole = Math.floor(value);
  const fraction = Math.round((value - whole) * 1000) / 1000;
  if (!thresholds.length) return Math.round(value / HALF_BAND) * HALF_BAND;
  for (const [threshold, step] of thresholds) if (fraction >= threshold) return whole + step;
  return whole;
}

/** Band of a copy: the mean of the criteria (a missing one counts 0), rounded. Null until all are rated. */
export function criteriaBand(keys: string[], bands: Record<string, string>, thresholds: [number, number][]): number | null {
  if (!keys.length || keys.some((k) => bands[k] === undefined || bands[k] === "")) return null;
  const sum = keys.reduce((acc, k) => acc + (Number(bands[k]) || 0), 0);
  return roundBand(sum / keys.length, thresholds);
}
